/**
 * The picture library, drawn once and used twice (FR-I13, ADR-0021):
 *
 * * **`pick`** — inside `MediaPickerDialog`, from any image field: click an
 *   image to put it in the field; upload one (button or drop) to put it in the
 *   field at once.
 * * **`manage`** — the **Website → Pictures** screen: a grid of cards, each with
 *   where it is used, its description for screen readers, and Delete.
 *
 * One component, so the two cannot come to disagree about what a firm's library
 * contains or how an upload is refused.
 *
 * **Wave 14D (C13, spec §10.5 item 7; T-4.64).** In `manage` mode a picture can
 * be **dropped anywhere on the page** — the whole window is the drop target,
 * which also stops a missed drop from opening the photograph in the tab and
 * taking the firm off ProDesk. **Delete while in use is refused with the
 * reason** (ADR-0021 §4): the button stays pressable and answers with the list
 * of places to replace it first, rather than being a greyed button whose reason
 * lives in a tooltip a phone never shows. The service refuses the same delete
 * regardless; this only says so before the request.
 */

import { Alert, Badge, Button, ConfirmDialog, Dialog, Input } from '@fiveox/ui'
import {
  Check,
  FileText,
  Globe,
  ImagePlus,
  Layers,
  Loader2,
  QrCode,
  Search,
  Sparkles,
  Trash2,
  UploadCloud,
  type LucideIcon,
} from 'lucide-react'
import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react'

import {
  ACCEPT,
  formatBytes,
  isStorageUnavailable,
  precheck,
  refusalText,
  siteMedia,
  uploadFailureText,
  type MediaUse,
  type SiteMediaItem,
} from '../../../lib/api/siteMedia'

const PAGE = 48

const USE_ICON: Record<MediaUse['kind'], LucideIcon> = {
  page: FileText,
  service: Layers,
  post: FileText,
  logo: Sparkles,
  favicon: Globe,
  booking: QrCode,
}

/** Files in a drag — `false` for text or a link, which are not uploads. */
function carriesFiles(event: { dataTransfer: DataTransfer | null }): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files')
}

export function MediaBrowser({
  mode,
  editable,
  onPick,
}: {
  mode: 'pick' | 'manage'
  /** `site:can_update`. Without it the library is read-only (FR-I13). */
  editable: boolean
  onPick?: (item: SiteMediaItem) => void
}) {
  const [items, setItems] = useState<SiteMediaItem[]>([])
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [deleting, setDeleting] = useState<SiteMediaItem | null>(null)
  const [busy, setBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const load = useCallback(async (query: string, offset = 0) => {
    setLoading(true)
    try {
      const body = await siteMedia.list({ q: query, offset, limit: PAGE })
      setItems((prev) => (offset === 0 ? body.data : [...prev, ...body.data]))
      setTotal(Number(body.meta?.total ?? body.data.length))
      setError(null)
    } catch (err) {
      setError(refusalText(err))
    } finally {
      setLoading(false)
    }
  }, [])

  // Searching waits for the firm to stop typing: a request per keystroke is a
  // list that flickers.
  useEffect(() => {
    const timer = setTimeout(() => void load(q), q ? 250 : 0)
    return () => clearTimeout(timer)
  }, [q, load])

  async function uploadFiles(files: FileList | File[] | null) {
    if (!files || files.length === 0) return
    setError(null)
    setNotice(null)
    const list = Array.from(files)
    let last: SiteMediaItem | null = null
    let added = 0
    // w8h, finding 4. The one refusal worth abandoning the rest of the
    // selection for: the store is missing or will not take a write, so the next
    // nine files fail identically. Ten copies of the same sentence with a
    // different filename in front of each is what makes a configuration fault
    // look like ten bad photographs.
    let storeIsDown = false
    for (const file of list) {
      const problem = precheck(file)
      if (problem) {
        setError(problem)
        continue
      }
      setUploading((n) => n + 1)
      try {
        const { item, existing } = await siteMedia.upload(file)
        last = item
        setItems((prev) => [item, ...prev.filter((p) => p.id !== item.id)])
        if (!existing) {
          setTotal((t) => t + 1)
          added += 1
        } else setNotice(`${file.name} is already in your library.`)
      } catch (err) {
        setError(uploadFailureText(file.name, err))
        storeIsDown = isStorageUnavailable(err)
      } finally {
        setUploading((n) => n - 1)
      }
      // After the `finally`, never inside the `catch`: a `break` in there runs
      // the `finally` too, and the counter would come down twice.
      if (storeIsDown) break
    }
    if (fileInput.current) fileInput.current.value = ''
    if (mode === 'manage' && added > 0) {
      setNotice(added === 1 ? 'Picture added to your library.' : `${added} pictures added to your library.`)
    }
    // In the picker, one upload *is* the choice — the firm came here to fill a
    // field, not to curate a library.
    if (mode === 'pick' && list.length === 1 && last) onPick?.(last)
  }

  // The latest `uploadFiles`, for the window listeners below, which are bound
  // once and would otherwise upload with the first render's closure.
  const upload = useRef(uploadFiles)
  upload.current = uploadFiles

  // `manage`: the whole window takes a drop. `dragenter`/`dragleave` fire for
  // every child crossed, so a depth counter says when the drag has really left.
  useEffect(() => {
    if (mode !== 'manage' || !editable) return
    let depth = 0
    const enter = (e: globalThis.DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      depth += 1
      setDragging(true)
    }
    const over = (e: globalThis.DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    }
    const leave = (e: globalThis.DragEvent) => {
      if (!carriesFiles(e)) return
      depth = Math.max(0, depth - 1)
      if (depth === 0) setDragging(false)
    }
    const drop = (e: globalThis.DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      depth = 0
      setDragging(false)
      void upload.current(e.dataTransfer?.files ?? null)
    }
    window.addEventListener('dragenter', enter)
    window.addEventListener('dragover', over)
    window.addEventListener('dragleave', leave)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragenter', enter)
      window.removeEventListener('dragover', over)
      window.removeEventListener('dragleave', leave)
      window.removeEventListener('drop', drop)
    }
  }, [mode, editable])

  // `pick`: the dialog's own area takes a drop (the page behind it is not the
  // firm's target there).
  const pickDrop =
    mode === 'pick' && editable
      ? {
          onDragOver: (e: DragEvent) => {
            if (!carriesFiles(e)) return
            e.preventDefault()
            setDragging(true)
          },
          onDragLeave: (e: DragEvent) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
            setDragging(false)
          },
          onDrop: (e: DragEvent) => {
            if (!carriesFiles(e)) return
            e.preventDefault()
            setDragging(false)
            void uploadFiles(e.dataTransfer.files)
          },
        }
      : {}

  async function saveAlt(item: SiteMediaItem, alt: string) {
    if ((item.alt ?? '') === alt.trim()) return
    try {
      const body = await siteMedia.setAlt(item.id, alt.trim())
      setItems((prev) => prev.map((p) => (p.id === item.id ? body.data : p)))
    } catch (err) {
      setError(refusalText(err))
    }
  }

  async function remove(item: SiteMediaItem) {
    setBusy(true)
    setDeleteError(null)
    try {
      await siteMedia.remove(item.id)
      setItems((prev) => prev.filter((p) => p.id !== item.id))
      setTotal((t) => t - 1)
      setDeleting(null)
    } catch (err) {
      setDeleteError(refusalText(err))
    } finally {
      setBusy(false)
    }
  }

  const choose = () => fileInput.current?.click()
  const picker = editable ? (
    <input
      ref={fileInput}
      type="file"
      accept={ACCEPT}
      multiple={mode === 'manage'}
      className="hidden"
      data-media-file=""
      onChange={(e) => void uploadFiles(e.target.files)}
    />
  ) : null

  const rules =
    'JPEG, PNG or WebP, up to 5 MB each. Location and camera details are removed, and large pictures are resized to 2400 px. These are for your public website — never put a client’s documents here.'

  const search = (
    <div className="relative min-w-0 flex-1">
      <Search
        size={16}
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 z-10 -translate-y-1/2"
        aria-hidden
      />
      <Input
        aria-label="Search images"
        placeholder="Search by name or description"
        className="pl-10"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
    </div>
  )

  const empty = !loading && items.length === 0

  if (mode === 'pick') {
    return (
      <div className={`space-y-3 rounded-xl ${dragging ? 'ring-primary ring-2' : ''}`} {...pickDrop}>
        <div className="flex flex-wrap items-center gap-2">
          {search}
          {picker}
          {editable && (
            <Button onClick={choose} disabled={uploading > 0}>
              {uploading > 0 ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
              {uploading > 0 ? 'Uploading…' : 'Upload'}
            </Button>
          )}
        </div>
        <p className="text-muted-foreground text-xs">
          {editable ? 'Or drop a picture here. ' : ''}
          {rules}
        </p>
        {error && <Alert tone="error">{error}</Alert>}
        {notice && <Alert tone="info">{notice}</Alert>}
        {empty ? (
          <div className="border-border text-muted-foreground rounded-xl border border-dashed p-8 text-center text-sm">
            {q ? 'No image matches that search.' : 'No images yet. Upload your first one.'}
          </div>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((item) => (
              <li key={item.id} className="border-border bg-surface-raised overflow-hidden rounded-xl border">
                <button
                  type="button"
                  className="focus-visible:ring-primary block w-full text-left focus:outline-none focus-visible:ring-2"
                  onClick={() => onPick?.(item)}
                  aria-label={`Use ${item.filename}`}
                >
                  <Thumb item={item} />
                  <Caption item={item} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <More loading={loading} shown={items.length} total={total} onMore={() => void load(q, items.length)} />
      </div>
    )
  }

  // The drop highlight covers the library, not the window: a hand-rolled
  // full-window layer is what `no-hand-rolled-modal.test.ts` refuses, and the
  // window listeners above take a drop anywhere regardless of what is drawn.
  return (
    <div className="relative space-y-5">
      {picker}
      {editable && (
        <div
          data-dropzone=""
          data-dragging={dragging ? 'true' : 'false'}
          className={`flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-colors sm:flex-row sm:text-left ${
            dragging ? 'border-primary bg-primary/5' : 'border-border bg-card'
          }`}
        >
          <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
            {uploading > 0 ? <Loader2 size={22} className="animate-spin" /> : <UploadCloud size={22} aria-hidden />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-foreground text-sm font-semibold">
              {uploading > 0
                ? `Uploading ${uploading === 1 ? 'a picture' : `${uploading} pictures`}…`
                : dragging
                  ? 'Drop to add them to your pictures'
                  : (
                      <>
                        <span className="hidden sm:inline">Drag pictures anywhere onto this page</span>
                        <span className="sm:hidden">Add pictures to your library</span>
                      </>
                    )}
            </p>
            <p className="text-muted-foreground mt-0.5 text-xs">{rules}</p>
          </div>
          <Button onClick={choose} disabled={uploading > 0} className="shrink-0">
            <ImagePlus size={15} aria-hidden /> Choose pictures
          </Button>
        </div>
      )}

      {error && <Alert tone="error">{error}</Alert>}
      {notice && <Alert tone="info">{notice}</Alert>}

      {(items.length > 0 || q) && (
        <div className="flex flex-wrap items-center gap-3">
          {search}
          <span className="text-muted-foreground shrink-0 text-sm tabular-nums" data-media-count="">
            {total} {total === 1 ? 'picture' : 'pictures'}
          </span>
        </div>
      )}

      {empty ? (
        <div className="border-border rounded-2xl border border-dashed p-10 text-center">
          <p className="text-foreground font-medium">{q ? 'No picture matches that search' : 'No pictures yet'}</p>
          <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">
            {q
              ? 'Try another word, or clear the search.'
              : 'Your office, your team, your city — pictures you upload here can go on any page of your site.'}
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3" aria-label="Pictures">
          {items.map((item) => (
            <PictureCard
              key={item.id}
              item={item}
              editable={editable}
              onAlt={(alt) => void saveAlt(item, alt)}
              onDelete={() => {
                setDeleteError(null)
                setDeleting(item)
              }}
            />
          ))}
        </ul>
      )}
      <More loading={loading} shown={items.length} total={total} onMore={() => void load(q, items.length)} />

      {/* Refused before asking: the service would refuse it too (ADR-0021 §4). */}
      <Dialog
        open={deleting !== null && deleting.uses.length > 0}
        onClose={() => setDeleting(null)}
        title="This picture is in use"
      >
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            <strong className="text-foreground font-medium">{deleting?.filename}</strong> can’t be deleted while your
            website uses it. Replace it in these places first, then delete it:
          </p>
          <UsesList uses={deleting?.uses ?? []} />
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setDeleting(null)}>
              OK
            </Button>
          </div>
        </div>
      </Dialog>
      <ConfirmDialog
        open={deleting !== null && deleting.uses.length === 0}
        onClose={() => setDeleting(null)}
        title="Delete this picture?"
        tone="danger"
        confirmLabel="Delete picture"
        cancelLabel="Keep it"
        busy={busy}
        busyLabel="Deleting…"
        error={deleteError}
        onConfirm={() => deleting && void remove(deleting)}
      >
        <p>
          <strong className="text-foreground font-medium">{deleting?.filename}</strong> leaves your library. Nothing on
          your website uses it.
        </p>
      </ConfirmDialog>

      {dragging && (
        <div
          aria-hidden
          className="bg-primary/10 border-primary pointer-events-none absolute -inset-2 z-20 rounded-2xl border-4 border-dashed"
        >
          {/* Sticky, so the firm sees it wherever the page is scrolled to. */}
          <div className="bg-background text-foreground sticky top-[45vh] mx-auto mt-48 flex w-fit items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold shadow-lg">
            <UploadCloud size={18} className="text-primary" /> Drop to add them to your pictures
          </div>
        </div>
      )}
    </div>
  )
}

function PictureCard({
  item,
  editable,
  onAlt,
  onDelete,
}: {
  item: SiteMediaItem
  editable: boolean
  onAlt: (alt: string) => void
  onDelete: () => void
}) {
  const inUse = item.uses.length > 0
  return (
    <li
      data-picture={item.id}
      className="border-border bg-surface-raised flex flex-col overflow-hidden rounded-xl border transition-shadow hover:shadow-sm"
    >
      <div className="relative">
        <Thumb item={item} />
        <div className="absolute left-2 top-2">
          {inUse ? (
            <Badge tone="success" size="sm">
              <Check size={11} aria-hidden /> In use
            </Badge>
          ) : (
            <span className="bg-background/90 text-muted-foreground rounded-full px-2 py-0.5 text-[11px] font-semibold">
              Not used
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-3">
        <Caption item={item} flush />
        <div data-uses="">
          {inUse ? (
            <>
              <p className="text-muted-foreground text-xs font-medium">Used on</p>
              <UsesList uses={item.uses} compact />
            </>
          ) : (
            <p className="text-muted-foreground text-xs">Not on your website yet.</p>
          )}
        </div>
        <Input
          size="sm"
          aria-label={`Description of ${item.filename}`}
          placeholder="Describe it for screen readers"
          defaultValue={item.alt ?? ''}
          disabled={!editable}
          onBlur={(e) => onAlt(e.target.value)}
        />
        {editable && (
          <div className="mt-auto flex justify-end">
            <Button variant="ghost" size="sm" onClick={onDelete} aria-label={`Delete ${item.filename}`}>
              <Trash2 size={14} aria-hidden /> Delete
            </Button>
          </div>
        )}
      </div>
    </li>
  )
}

function UsesList({ uses, compact = false }: { uses: MediaUse[]; compact?: boolean }) {
  return (
    <ul className={compact ? 'mt-1 space-y-0.5' : 'border-border space-y-1.5 rounded-xl border p-3'}>
      {uses.map((use, i) => {
        const Icon = USE_ICON[use.kind] ?? FileText
        return (
          <li key={`${use.kind}-${use.label}-${i}`} className="text-foreground flex min-w-0 items-center gap-1.5 text-xs">
            <Icon size={12} className="text-muted-foreground shrink-0" aria-hidden />
            <span className="truncate">{use.label}</span>
            {use.where && (
              <span className="text-muted-foreground shrink-0">· {use.where === 'live' ? 'live site' : 'draft'}</span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function More({ loading, shown, total, onMore }: { loading: boolean; shown: number; total: number; onMore: () => void }) {
  if (loading) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </p>
    )
  }
  if (shown >= total) return null
  return (
    <Button variant="ghost" onClick={onMore}>
      Show more ({total - shown})
    </Button>
  )
}

function Thumb({ item }: { item: SiteMediaItem }) {
  return (
    <div className="bg-muted aspect-[4/3]">
      <img
        src={item.url}
        alt={item.alt ?? ''}
        width={item.width}
        height={item.height}
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </div>
  )
}

function Caption({ item, flush = false }: { item: SiteMediaItem; flush?: boolean }) {
  return (
    <div className={flush ? 'min-w-0' : 'px-2 py-1.5'}>
      <p className="text-foreground truncate text-sm font-medium" title={item.filename}>
        {item.filename}
      </p>
      <p className="text-muted-foreground text-[11px]">
        {item.width} × {item.height} · {formatBytes(item.size_bytes)}
      </p>
    </div>
  )
}

export function MediaPickerDialog({
  open,
  onClose,
  onPick,
  editable,
}: {
  open: boolean
  onClose: () => void
  onPick: (item: SiteMediaItem) => void
  editable: boolean
}) {
  return (
    <Dialog open={open} onClose={onClose} title="Choose an image" width="xl">
      {open ? (
        <MediaBrowser
          mode="pick"
          editable={editable}
          onPick={(item) => {
            onPick(item)
            onClose()
          }}
        />
      ) : null}
    </Dialog>
  )
}
